import { defineStore } from 'pinia';
import type { PrintProductItem } from '@/types/utils/print.utils.types';

interface PrintStoreState {
    isPrinting: boolean;
    selectedPrintItems: PrintProductItem[];
}

const usePrintStoreState = defineStore('gh-print-store', {
    state: (): PrintStoreState => ({
        isPrinting: false,
        selectedPrintItems: [],
    }),
    actions: {
        setPrintStatus(value: boolean): void {
            this.isPrinting = value;
        },
        togglePrintMode(): void {
            this.isPrinting = !this.isPrinting;
        },
        isPrintItemsExists(productClass: PrintProductItem): boolean {
            const matchId =
                productClass.FavoriteID || productClass.ProductClassID;

            return this.selectedPrintItems.some((item) => {
                const itemId = item.FavoriteID || item.ProductClassID;
                return itemId === matchId;
            });
        },
        togglePrintItem(productClass: PrintProductItem): boolean {
            const matchId =
                productClass.FavoriteID || productClass.ProductClassID;

            const index = this.selectedPrintItems.findIndex((item) => {
                const itemId = item.FavoriteID || item.ProductClassID;
                return itemId === matchId;
            });

            if (index > -1) {
                this.selectedPrintItems.splice(index, 1);
                return false;
            }

            const {
                FavoriteID,
                ProductClassID,
                ProductClassName,
                ProductCode,
                Description,
                ImgUrl,
                Specifications = '',
            } = productClass;

            this.selectedPrintItems.push({
                FavoriteID,
                ProductClassID,
                ProductClassName,
                ProductCode,
                Description,
                ImgUrl,
                Specifications,
            });

            return true;
        },
        removeAllPrintItems(): void {
            this.selectedPrintItems = [];
        },
        addAllPrintItems(productSubMains: PrintProductItem[]): void {
            const items = Object.values(
                productSubMains.reduce<Record<string, PrintProductItem>>(
                    (acc, item) => {
                        const productClassId = String(
                            item.ProductClassID ?? ''
                        );
                        if (!productClassId || acc[productClassId]) {
                            return acc;
                        }

                        acc[productClassId] = {
                            FavoriteID: item.FavoriteID,
                            ProductClassID: item.ProductClassID,
                            ProductClassName: item.ProductClassName,
                            ProductCode: item.ProductCode,
                            Description: item.Description,
                            ImgUrl: item.ImgUrl,
                            Specifications: item.Specifications || '',
                        };
                        return acc;
                    },
                    {}
                )
            );

            this.selectedPrintItems = items;
        },
    },
});

type PrintStorePiniaState = ReturnType<typeof usePrintStoreState>;

/**
 * 取得 Pinia print store
 */
export const useAppPrintStore = (): PrintStorePiniaState => {
    return usePrintStoreState();
};
